import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getClient, getClientInvoices, getClientStats, downloadInvoicePdf } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, FileText, Download, Eye, Mail, Phone, MapPin, Building, TrendingUp, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount, currency = 'CHF') => new Intl.NumberFormat('fr-CH', { style: 'currency', currency }).format(amount);
const formatDate = (dateString) => new Date(dateString).toLocaleDateString('fr-CH');

const StatusBadge = ({ status }) => {
  const styles = {
    paid: 'badge-paid',
    sent: 'bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full text-xs font-medium',
    draft: 'bg-slate-50 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-full text-xs font-medium',
    overdue: 'badge-overdue',
    cancelled: 'bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full text-xs font-medium'
  };
  const labels = { paid: 'Payée', sent: 'Envoyée', draft: 'Brouillon', overdue: 'En retard', cancelled: 'Annulée' };
  return <span className={styles[status] || styles.draft}>{labels[status] || status}</span>;
};

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clientRes, invoicesRes, statsRes] = await Promise.all([
        getClient(id), 
        getClientInvoices(id),
        getClientStats(id)
      ]);
      setClient(clientRes.data);
      setInvoices(invoicesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur');
      navigate('/app/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (invoiceId) => {
    try {
      const response = await downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  if (!client) return null;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/app/clients')}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-blue-50 flex items-center justify-center">
                  <span className="text-blue-700 font-bold text-2xl">{client.name.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <CardTitle className="font-heading text-xl">{client.name}</CardTitle>
                  {client.vat_number && <p className="text-sm text-slate-500">TVA: {client.vat_number}</p>}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span>{client.address}, {client.postal_code} {client.city}</span>
              </div>
              {client.email && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4 text-slate-400" /><span>{client.email}</span>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4 text-slate-400" /><span>{client.phone}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Internal Notes */}
          {client.internal_notes && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Notes internes
                </p>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{client.internal_notes}</p>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5" />Résumé financier</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {stats ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Total facturé</span>
                    <span className="font-bold text-lg">{formatCurrency(stats.total_invoiced)}</span>
                  </div>
                  <div className="flex justify-between items-center text-green-600">
                    <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" />Payé</span>
                    <span className="font-bold">{formatCurrency(stats.total_paid)}</span>
                  </div>
                  {stats.total_credited > 0 && (
                    <div className="flex justify-between items-center text-orange-600">
                      <span>Avoirs</span>
                      <span className="font-bold">-{formatCurrency(stats.total_credited)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-4 items-center">
                    <span className="text-slate-600 font-medium">Solde dû</span>
                    <span className={`font-bold text-lg ${stats.total_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(stats.total_due)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t">
                    <div className="text-center p-2 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-700">{stats.invoices_count}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{stats.invoices_paid}</p>
                      <p className="text-xs text-green-600">Payées</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-700">{stats.invoices_pending + stats.invoices_overdue}</p>
                      <p className="text-xs text-amber-600">En attente</p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-slate-500 text-center py-4">Chargement...</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Historique des factures</CardTitle>
              <Button onClick={() => navigate('/app/invoices/new')} className="bg-blue-600 hover:bg-blue-700" size="sm">
                Nouvelle facture
              </Button>
            </CardHeader>
            <CardContent>
              {invoices.length > 0 ? (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex-1">
                        <Link to={`/app/invoices/${invoice.id}`} className="font-medium text-slate-900 hover:text-blue-600">
                          {invoice.invoice_number}
                        </Link>
                        <p className="text-sm text-slate-500">{formatDate(invoice.invoice_date || invoice.created_at)}</p>
                      </div>
                      <div className="text-right mx-4">
                        <p className="font-medium">{formatCurrency(invoice.total, invoice.currency)}</p>
                        {invoice.amount_due > 0 && invoice.amount_due < invoice.total && (
                          <p className="text-xs text-amber-600">Reste: {formatCurrency(invoice.amount_due, invoice.currency)}</p>
                        )}
                        <StatusBadge status={invoice.status} />
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/app/invoices/${invoice.id}`)} title="Voir détails"><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleViewPdf(invoice.id)} title="Voir PDF"><Download className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">Aucune facture pour ce client</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDetailPage;
